<template>
    <div class="ui-page">
        <ui-page-header title="Impugnações" subtitle="Impugnações e incidentes judiciais cadastrados nos casos.">
            <!-- Incidentes de credito capturados no DJEN e ainda nao cadastrados -->
            <djen-new-button base="/api/admin/proceedings" @changed="load()" />
            <!-- Exporta em Excel tudo o que esta cadastrado nas impugnacoes do filtro atual -->
            <v-btn id="btnExport" type="button" color="primary" outlined :loading="exportLoading" @click="exportXlsx">
                <v-icon left small>mdi-file-excel-outline</v-icon>
                Exportar Excel
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" type="button" color="primary" depressed to="/impugnacoes/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <!-- Publicacoes do DJEN ainda sem ciencia: filtra as impugnacoes com novidade -->
        <div v-if="djen.unread_publications" class="djen-cards">
            <button type="button" class="djen-card" :class="{ 'djen-card--active': dataList.filter.djen_unread }" @click="toggleDjenFilter">
                <span class="djen-card__label"><v-icon small class="mr-1">mdi-newspaper-variant-outline</v-icon>Publicações novas no DJEN</span>
                <span class="djen-card__value">{{ djen.unread_publications }}</span>
            </button>
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field dense placeholder="Buscar pelo nº do processo" v-model="dataList.filter.process_number"
                    prepend-inner-icon="mdi-magnify" hide-details outlined clearable class="ui-toolbar__search"
                    @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete dense :items="dataset.clients" placeholder="Todos os casos" class="ui-toolbar__field"
                    v-model="dataList.filter.client_id" item-value="id" item-text="name" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable no-data-text="Nenhum caso encontrado" @change="applyFilters">
                </v-autocomplete>
                <v-select dense :items="dataset.proceedingTypesFilter" placeholder="Todos os tipos" class="ui-toolbar__field"
                    v-model="dataList.filter.type" item-value="id" item-text="name" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable @change="applyFilters">
                </v-select>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data"
                :server-items-length="dataList.count" :options.sync="dataList.options"
                @update:options="load()" disable-sort hide-default-footer :loading="dataList.loading"
                no-data-text="Nenhuma impugnação foi encontrada" loading-text="Carregando dados..."
                class="ui-clickable-rows" @click:row="viewItem">

                <template v-slot:[`item.process_number`]="{ item }">
                    <span class="ui-cell-main">{{ item.process_number }}</span>
                    <span v-if="item.djen_unread" class="djen-row-badge" title="Publicações do DJEN ainda sem ciência"><v-icon x-small color="#b45309">mdi-newspaper-variant-outline</v-icon> {{ item.djen_unread }} nova(s)</span>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    <ui-badge tone="primary" :dot="false">{{ item.type | enum(dataset.proceedingTypes, "id", "name") }}</ui-badge>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    {{ item.client ? item.client.name : '-' }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="statusLabels[item.status] || 'Em andamento'" :color="item.status === 1 ? 'success' : 'blue'" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => viewItem(item) , disabled: !$allowed('edit') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => deleteItem(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <!-- Delete confirmation dialog -->
        <v-dialog v-model="deleteDialog" max-width="400px">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Aviso</h3>
                </div>
                <div class="ui-card__body">
                    Tem certeza que deseja excluir a impugnação
                    <strong v-if="itemToDelete">{{ itemToDelete.process_number }}</strong>?
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="deleteDialog = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('delete')" color="error" depressed @click="confirmDelete" :loading="dataList.loading">Excluir</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import proceedingTypes from "@/common/data/proceeding-types.data";
import participantRoles from "@/common/data/proceeding-participant-roles.data";
import appealResults from "@/common/data/proceeding-appeal-results.data";
import pretensionTypes from "@/common/data/incident-pretension-types.data";
import creditorTypes from "@/common/data/creditor-types.data";
import currencyTypes from "@/common/data/currency-types.data";
import { downloadXlsxSheets } from "@/common/util/export-xlsx.util";
import DjenNewButton from "@/components/djen/DjenNewButton.vue";

export default {
    name: "ProceedingList",

    components: { DjenNewButton },

    data: () => ({
        deleteDialog: false,
        itemToDelete: null,
        exportLoading: false,

        statusLabels: {
            0: "Em andamento",
            1: "Finalizado",
        },

        dataset: {
            clients: [],
            proceedingTypes: proceedingTypes,
            proceedingTypesFilter: proceedingTypes,
        },

        // Publicacoes do DJEN sem ciencia (card que filtra as impugnacoes com novidade).
        djen: { unread_publications: 0, proceedings: 0 },

        dataList: {
            filter: {
                client_id: null,
                process_number: null,
                type: null,
                djen_unread: null,
            },
            headers: [
                { text: "Nº Processo", value: "process_number", align: "start" },
                { text: "Tipo", value: "type" },
                { text: "Caso", value: "client" },
                { text: "Status", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            },
        },
    }),

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },
    },

    created() {
        this.loadClients();
    },

    methods: {
        applyFilters() {
            this.load(true);
        },

        toggleDjenFilter() {
            this.dataList.filter.djen_unread = this.dataList.filter.djen_unread ? null : true;
            this.applyFilters();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = {
                client_id: null,
                process_number: null,
                type: null,
                djen_unread: null,
            };
            this.applyFilters();
        },

        // O @update:options da tabela recarrega a lista.
        pageChanged(page) {
            this.dataList.options.page = page;
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        async load(resetPage = false) {
            if (resetPage) {
                this.dataList.options.page = 1;
            }

            let searchPayload = Object.fromEntries(
                Object.entries(this.dataList.filter).filter(([_, v]) => v !== null && v !== undefined && v !== "")
            );

            this.dataList.loading = true;

            try {
                const params = {
                    page: this.dataList.options.page,
                    limit: this.dataList.options.itemsPerPage,
                    search: searchPayload,
                };

                const response = await axios.get('/api/admin/proceedings', { params });
                const content = response.data.content || response.data;

                this.dataList.data = content.data || [];
                this.dataList.count = content.total || (content.paging ? content.paging.total : 0);
                if (content.djen) this.djen = content.djen;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        // Excel com uma aba resumo (uma linha por impugnacao) e abas de detalhe
        // (participantes/creditos, manifestacoes, creditos da sentenca, recursos, comentarios).
        async exportXlsx() {
            this.exportLoading = true;
            try {
                const search = Object.fromEntries(
                    Object.entries(this.dataList.filter).filter(([_, v]) => v !== null && v !== undefined && v !== "")
                );
                const response = await axios.get('/api/admin/proceedings', { params: { all: 1, search } });
                const content = response.data.content || response.data;
                const items = content.data || [];
                if (!items.length) {
                    this.$dialog.notify.info('Nenhuma impugnação para exportar com os filtros aplicados.', { position: 'top-right', timeout: 3000 });
                    return;
                }

                const label = (list, id) => { const found = list.find((x) => Number(x.id) === Number(id)); return found ? found.name : (id === null || id === undefined || id === '' ? '' : String(id)); };
                const bool = (value) => ([true, 1, '1'].includes(value) ? 'Sim' : 'Não');
                const num = (value) => (value === null || value === undefined || value === '' ? '' : Number(value));
                const date = (value, format = 'DD/MM/YYYY HH:mm') => (value && value !== '0000-00-00 00:00:00' ? this.$moment(value).format(format) : '');
                const modeLabel = (mode) => ({ detailed: 'Detalhado', summary: 'Resumido', 1: 'Detalhado', 2: 'Resumido' })[mode] || (mode || '');
                const ident = (p) => ({ 'ID': p.id, 'Nº PROCESSO': p.process_number, 'CASO': p.client ? p.client.name : '' });

                const summary = [];
                const participants = [];
                const manifestations = [];
                const sentenceCredits = [];
                const appeals = [];
                const comments = [];

                items.forEach((p) => {
                    const creditors = (p.participants || []).filter((x) => Number(x.role) === 1);
                    const effectiveAppeal = (p.appeals || []).slice().reverse().find((a) => a.changed_credit) || (p.appeals || []).slice(-1)[0] || null;
                    summary.push({
                        ...ident(p),
                        'TIPO': label(this.dataset.proceedingTypes, p.type),
                        'MODO': modeLabel(p.mode),
                        'STATUS': this.statusLabels[p.status] || p.status,
                        'CREDOR(ES)': creditors.map((x) => x.name).join(' | '),
                        'CPF/CNPJ': creditors.map((x) => x.registration || '').join(' | '),
                        'PARTICIPANTES': (p.participants || []).length,
                        'CRÉDITOS DISCUTIDOS': (p.participants || []).reduce((n, x) => n + ((x.credits || []).length), 0),
                        'VALOR DISCUTIDO (R$)': (p.participants || []).reduce((sum, x) => sum + (x.credits || []).reduce((s, c) => s + (Number(c.value) || 0), 0), 0),
                        'MANIFESTAÇÕES': (p.manifestations || []).length,
                        'HOUVE SENTENÇA': bool(p.has_sentence),
                        'RESULTADO DA SENTENÇA': p.sentence_result || '',
                        'SENTENÇA ALTEROU CRÉDITO': bool(p.sentence_changed_credit),
                        'SENTENÇA - VALOR FINAL': num(p.sentence_final_value),
                        'SENTENÇA - CLASSE FINAL': label(creditorTypes, p.sentence_final_class),
                        'SENTENÇA - MOEDA': label(currencyTypes, p.sentence_final_currency),
                        'HOUVE RECURSO': bool(p.has_appeal),
                        'RECURSOS': (p.appeals || []).map((a) => `${a.name || 'Recurso'}: ${label(appealResults, a.result) || 'sem resultado'}`).join(' | '),
                        'RECURSO EFETIVO': effectiveAppeal ? (effectiveAppeal.name || 'Recurso') : '',
                        'RECURSO ALTEROU CRÉDITO': bool(p.appeal_changed_credit),
                        'RECURSO - VALOR FINAL': num(p.appeal_final_value),
                        'RECURSO - CLASSE FINAL': label(creditorTypes, p.appeal_final_class),
                        'RECURSO - MOEDA': label(currencyTypes, p.appeal_final_currency),
                        'TRÂNSITO EM JULGADO': bool(p.final_judgment),
                        'QGC ATUALIZADO': bool(p.qgc_updated),
                        'COMENTÁRIOS': (p.comments || []).length,
                        'CRIADO EM': date(p.created_at),
                        'ATUALIZADO EM': date(p.updated_at),
                    });

                    (p.participants || []).forEach((x) => {
                        const credits = (x.credits || []).length ? x.credits : [null];
                        credits.forEach((c) => {
                            participants.push({
                                ...ident(p),
                                'PARTICIPANTE': x.name,
                                'PAPEL': label(participantRoles, x.role),
                                'CPF/CNPJ': x.registration || '',
                                'POSIÇÃO': x.position || '',
                                'VINCULADO AO QGC': x.user_creditor_id ? 'Sim' : 'Não',
                                'CRÉDITO NO QGC (R$)': x.userCreditor ? num(x.userCreditor.amount) : '',
                                'VALOR DISCUTIDO': c ? num(c.value) : '',
                                'CLASSE': c ? label(creditorTypes, c.class) : '',
                                'MOEDA': c ? label(currencyTypes, c.currency) : '',
                                'PRETENSÃO': c ? label(pretensionTypes, c.pretension) : '',
                            });
                        });
                    });

                    (p.manifestations || []).forEach((m) => {
                        manifestations.push({
                            ...ident(p),
                            'PARTICIPANTE': m.participant ? m.participant.name : '',
                            'REFERÊNCIA': m.reference || m.ref || '',
                            'DESCRIÇÃO': m.description || '',
                            'VALOR': num(m.credit_value),
                            'CLASSE': label(creditorTypes, m.credit_class),
                            'MOEDA': label(currencyTypes, m.credit_currency),
                            'DATA': date(m.created_at),
                        });
                    });

                    (p.sentenceCredits || p.sentence_credits || []).forEach((s) => {
                        sentenceCredits.push({
                            ...ident(p),
                            'PARTICIPANTE': s.participant ? s.participant.name : '',
                            'VALOR FINAL': num(s.value),
                            'CLASSE FINAL': label(creditorTypes, s.class),
                            'MOEDA': label(currencyTypes, s.currency),
                        });
                    });

                    (p.appeals || []).forEach((a, index) => {
                        appeals.push({
                            ...ident(p),
                            'Nº': index + 1,
                            'RECURSO': a.name || '',
                            'RESULTADO': label(appealResults, a.result),
                            'DATA DA DECISÃO': date(a.decided_at, 'DD/MM/YYYY'),
                            'ALTEROU CRÉDITO': bool(a.changed_credit),
                            'VALOR FINAL': num(a.final_value),
                            'CLASSE FINAL': label(creditorTypes, a.final_class),
                            'MOEDA': label(currencyTypes, a.final_currency),
                            'PREVALECE PARA O QGC': effectiveAppeal && effectiveAppeal.id === a.id ? 'Sim' : 'Não',
                        });
                    });

                    (p.comments || []).forEach((c) => {
                        comments.push({
                            ...ident(p),
                            'AUTOR': c.admin ? c.admin.name : '',
                            'COMENTÁRIO': c.content || '',
                            'DATA': date(c.created_at),
                        });
                    });
                });

                await downloadXlsxSheets([
                    { name: 'Impugnações', rows: summary },
                    { name: 'Participantes e créditos', rows: participants },
                    { name: 'Manifestações', rows: manifestations },
                    { name: 'Créditos da sentença', rows: sentenceCredits },
                    { name: 'Recursos', rows: appeals },
                    { name: 'Comentários', rows: comments },
                ], `impugnacoes-${this.$moment().format('YYYY-MM-DD')}`);
                this.$dialog.notify.success(`${items.length} impugnação(ões) exportada(s).`, { position: 'top-right', timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao exportar', { position: 'top-right', timeout: 5000 });
            } finally {
                this.exportLoading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get('/api/clients', { params: { start: 1, limit: 100000, search: {} } });
                const data = result.data.content.data;
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        viewItem(item) {
            this.$router.push(`/impugnacoes/${item.id}`);
        },

        deleteItem(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            if (!this.itemToDelete) return;

            this.dataList.loading = true;

            try {
                await axios.delete(`/api/admin/proceedings/${this.itemToDelete.id}`);

                this.$dialog.notify.info("Impugnação excluída com sucesso", { position: "top-right", timeout: 2000 });
                this.deleteDialog = false;
                this.itemToDelete = null;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            } finally {
                this.dataList.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.djen-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 320px)); gap: 10px; margin-bottom: 16px; }
.djen-card { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; text-align: left; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
.djen-card:hover { border-color: #98a2b3; }
.djen-card--active { border-color: #27306b; box-shadow: 0 0 0 1px #27306b inset; }
.djen-card__label { display: flex; align-items: center; font-size: 12.5px; color: #475467; }
.djen-card__value { font-size: 20px; font-weight: 700; color: #b45309; }
.djen-row-badge { display: inline-flex; align-items: center; gap: 3px; margin-left: 8px; padding: 0 8px; border-radius: 999px; background: #fef3c7; color: #b45309; font-size: 11px; font-weight: 700; line-height: 18px; white-space: nowrap; }
</style>
